<script lang="ts" context="module">
    import type { ToastData } from '$components/Toast/ToastData';

    export type ActionReturn =
        | { type: 'error'; message: string }
        | {
              type: 'success';
              toast?: ToastData;
          };

    export type Action =
        | {
              type: 'async';
              action: (key: string) => Promise<ActionReturn>;
          }
        | {
              type: 'sync';
              action: (key: string) => ActionReturn;
          };

    export type Actions = {
        [key in string]?: Action | null | undefined;
    };
</script>

<script lang="ts">
    import { fly } from 'svelte/transition';
    import { quadOut, quartInOut } from 'svelte/easing';
    import { match, P } from 'ts-pattern';
    import { setButtonMenuContext } from '$components/ButtonMenu/Context.js';
    import { derived, type Writable, writable } from 'svelte/store';
    import { getMessageFromError } from '$lib/utils/error.js';
    import { getContainer } from '$components/Context/ContainerContext.js';
    import { createDropdownMenu, melt } from '@melt-ui/svelte';
    import { cx } from '$lib/utils/cx';

    function transitionIn(node: Element) {
        return fly(node, {
            delay: 0,
            duration: 300,
            easing: quadOut,
            y: -10,
            opacity: 0.5,
        });
    }

    function transitionOut(node: Element) {
        return fly(node, {
            delay: 0,
            duration: 300,
            easing: quartInOut,
            y: 10,
            opacity: 0,
        });
    }

    let className: string = '';
    export { className as class };

    export let zIndexClass = 'z-1';

    export let actions: Actions = {};

    export let defaultAction: Action | null = null;

    export let ariaLabel: string | undefined | null = null;

    let loadingSet: Writable<Set<string>> = writable(new Set());
    let allDisabled = writable(false);

    const {
        helpers: { addToast },
    } = getContainer().toaster();

    async function handleAction(key: string, action: Action) {
        let res = await match(action)
            .with({ type: 'sync' }, async ({ action }) => action(key))
            .with({ type: 'async' }, async ({ action }) => {
                $loadingSet.add(key);
                $loadingSet = $loadingSet;
                $allDisabled = true;
                try {
                    return await action(key);
                } catch (e) {
                    return {
                        type: 'error' as const,
                        message: getMessageFromError(e),
                    };
                } finally {
                    $loadingSet.delete(key);
                    $allDisabled = false;
                    $loadingSet = $loadingSet;
                }
            })
            .exhaustive();

        match(res)
            .with({ type: 'success' }, (res) => {
                $open = false;
                if (res.toast != null) {
                    addToast({ data: { type: 'success', ...res.toast } });
                }
            })
            .with({ type: 'error', message: P.select() }, (message: string) => {
                addToast({ data: { message, type: 'error' } });
            })
            .exhaustive();
    }

    const meltMenu = createDropdownMenu({
        forceVisible: true,
        loop: true,
        closeOnItemClick: false,
    });
    const {
        elements: { trigger, menu },
        states: { open },
    } = meltMenu;

    function onClick(actionKey: string) {
        const action = actions[actionKey] ?? null;
        if (action != null) {
            handleAction(actionKey, action);
        } else if (defaultAction != null) {
            handleAction(actionKey, defaultAction);
        }
    }

    let context = {
        isLoading: derived(
            loadingSet,
            (loadingSet: Set<string>) => (key: string) => loadingSet.has(key),
        ),
        meltMenu,
        allDisabled,
        onClick,
    };

    setButtonMenuContext(context);
</script>

<button use:melt={$trigger} class={className} aria-label={ariaLabel}>
    <slot name="button" />
</button>

{#if $open}
    <div
        use:melt={$menu}
        in:transitionIn
        out:transitionOut
        class={cx(
            'p-px bg-white border border-solid border-black/20 rounded shadow-xl',
            zIndexClass,
        )}
    >
        <ul class="mx-0 my-2 p-0 list-none">
            <slot name="menu" />
        </ul>
    </div>
{/if}
